import { CloudDownload } from '@mui/icons-material';
import { Alert, Box, Theme, Typography } from '@mui/material';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Button, ButtonProgressSpinner } from '@/components';
import { TrackerMessages, urls } from '@/constants';
import { Link } from '@/containers';
import { LumiQCombinationMarkIcon } from '@/icons';
import {
  downloadCertificate,
  downloadCertificateSummary,
  selectUserCompliance,
  useAppDispatch,
} from '@/store';
import { SnackbarUtils } from '@/utils';

type AccessToken = JwtPayload & {
  access?: {
    scopes: string[];
    pdHourId: number;
    fromDate: string;
    toDate: string;
  };
};

export const CertificateDownload = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const compliance = useSelector(selectUserCompliance);
  const [isCertificateDownloading, setIsCertificateDownloading] =
    useState(false);
  const [isSummaryDownloading, setIsSummaryDownloading] = useState(false);

  const isLoading = isSummaryDownloading || isCertificateDownloading;

  const query = new URLSearchParams(location.search);
  const { token } = Object.fromEntries(query.entries());

  let decodedToken: AccessToken = {};
  let errMsg;

  try {
    decodedToken = jwtDecode<AccessToken>(token);
    if (Date.now() > (decodedToken?.exp || 0) * 1000) {
      errMsg = 'Token Expired';
    }
  } catch {
    errMsg = 'Invalid Token';
  }

  const { access } = decodedToken;
  const isSingleCertificateDownload = access?.scopes?.includes(
    'downloadCertificate',
  );

  const onDownload = async () => {
    try {
      if (isSingleCertificateDownload) {
        setIsCertificateDownloading(true);
        await dispatch(
          downloadCertificate({
            certificateId: access!.pdHourId,
          }),
        ).unwrap();
      } else {
        setIsSummaryDownloading(true);
        await dispatch(
          downloadCertificateSummary({
            fromDate: access!.fromDate,
            toDate: access!.toDate,
          }),
        ).unwrap();
      }
    } catch {
      SnackbarUtils.error(TrackerMessages.DOWNLOAD_ERROR);
    } finally {
      setIsCertificateDownloading(false);
      setIsSummaryDownloading(false);
    }
  };

  return (
    <>
      <Box sx={styles.header}>
        <LumiQCombinationMarkIcon sx={styles.logo} />
      </Box>
      <Box sx={styles.container}>
        <Typography variant="h3">Download Documentation</Typography>
        <Box sx={styles.paper}>
          {!errMsg ? (
            <Alert severity="error">{errMsg}</Alert>
          ) : (
            <>
              <Typography>
                You can download the {compliance} documentation you requested by
                clicking the button below.
              </Typography>
              <Box sx={styles.buttonContainer}>
                <Button
                  label="Download"
                  startIcon={
                    isLoading ? <ButtonProgressSpinner /> : <CloudDownload />
                  }
                  fullWidth={false}
                  disabled={isLoading}
                  onClick={onDownload}
                />
              </Box>
              <Typography component="span">
                If you require any assistance, or if you have any feedback to
                share, please don&apos;t hesitate to contact us at&nbsp;
              </Typography>
              <Link inline to={`mailto:${urls.supportEmail}`}>
                {urls.supportEmail}
              </Link>
              <Typography component="span">.</Typography>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: (theme: Theme) => theme.palette.gradients.primary,
    height: 60,
    width: '100%',
  },
  logo: {
    width: 120,
    color: 'white',
  },
  container: {
    p: 3,
    maxWidth: 1160,
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    mx: 'auto',
  },
  paper: {
    mt: 3,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'grey50',
    boxShadow: (theme: Theme) => `0 2px 5px 0 ${theme.palette.grey50}`,
    boxSizing: 'border-box',
    p: 3,
    width: '100%',
  },
  buttonContainer: {
    my: 3,
  },
};
